<template>
  <div class="imageset-group">
    <div class="imageset-group__header">
      <template v-if="!imageset.remain">
        <router-link :to="imagesetLink">
          <Status :status="imageset.report_status" />
        </router-link>
        <div class="imageset-group__info">
          <div class="imageset-group__title">
            {{ imageset.activity_category_name }}
          </div>
          <div class="imageset-group__subtitle">
            {{ imageset.image_set_type_name }}
          </div>
        </div>
      </template>
      <div v-else>
        <el-button
          class="imageset-group__button"
          icon="el-icon-more"
          style="cursor: auto;" />
      </div>
      <div v-if="imageset.gonnaRemain" class="imageset-group__buttons">
        <el-button
          class="imageset-group__button"
          icon="el-icon-more"
          style="cursor: auto;" />
      </div>
      <div v-else class="imageset-group__buttons">
        <el-button
          v-if="imageset.report_status !== 'discarded' && imageset.mosaic"
          class="imageset-group__button"
          type="text"
          @click="panoramaPopupStatus = true">
          <svg style="width: 24px; height: 24px;" viewBox="0 0 24 24">
            <!-- eslint-disable-next-line -->
              <path fill="currentColor" d="M21 4C20.4 4 17.4 5.5 12 5.5C6.7 5.5 3.5 4 3 4C2.5 4 2 4.4 2 5V19C2 19.6 2.5 20 3 20C3.6 20 6.5 18.5 12 18.5C17.4 18.5 20.4 20 21 20C21.5 20 22 19.6 22 19V5C22 4.4 21.5 4 21 4M20 17.6C18 17 15.4 16.5 12 16.5C8.6 16.5 6 17 4 17.6V6.4C6.6 7.1 9.3 7.5 12 7.5C15.4 7.5 18 7 20 6.4V17.6M9.2 11L5.5 15.4C7.5 15.1 9.7 15 12 15C14.3 15 16.5 15.1 18.5 15.4L14 10L11.2 13.4L9.2 11Z" />
          </svg>
        </el-button>
        <template v-if="isReportInspectorActive">
          <el-button
            class="imageset-group__button"
            :disabled="reportInspector.loading"
            @click="reqReportInspector">
            <img v-if="!reportInspector.loading"
                 src="/statics/modern-imageset-icons/report-inspector.svg"
                 alt="report-inspector">
            <i v-else class="el-icon-loading" />
          </el-button>
          <ReportInspector v-if="isReportInspectorRequested"
                           :report-data="reportInspector.data"
                           :project-id="projectId"
                           :visit-id="visitId"
                           :skus-data="reportInspector.skusData"
                           :report-skus-id-list="reportSkusIDList">
            <template #default="{openReportInspectorDialog}">
              <button ref="reportInspectorButton"
                      class="hide"
                      @click="openInspector(openReportInspectorDialog)" />
            </template>
          </ReportInspector>
        </template>
      </div>
    </div>
    <div class="imageset-group__list">
      <ImageItem v-for="image in imageset.images"
                 :key="image.id"
                 :image="image"
                 class="imageset-group__image"
                 :style="`width: ${imageBoxWidth}px;`"
                 @open-popup="openPopup" />
    </div>
    <el-dialog class="imageset-dialog"
               top="5vh"
               :width="isMobile ? '95%' :'40%'"
               :visible.sync="popupStatus">
      <ImagesetPopup v-model="activeImage"
                     :store="store"
                     :imageset="imageset"
                     :is-report-inspector-active="isReportInspectorActive"
                     @reqReportInspector="reqReportInspector"
                     @openPanorama="panoramaPopupStatus = true" />
    </el-dialog>
    <el-dialog class="imageset-dialog"
               top="5vh"
               :width="isMobile ? '95%' :'65%'"
               :visible.sync="panoramaPopupStatus">
      <ImagesetPanorama
        :store="store"
        :imageset="imageset"
        :is-report-inspector-active="isReportInspectorActive"
        @reqReportInspector="reqReportInspector" />
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Status from '@/components/Status';
import { fetchSkus } from '@/shared/apis';
import { fetchImageset } from '@/modules/imageset/api';
import ReportInspector from '@/modules/report-inspector';
import mobileScreenControl from '@/helpers/mixins/mobileScreenControl';
import breadcrumbs from '@/router/breadcrumbs';
import ImageItem from './ImageItem';
import ImagesetPopup from './ImagesetPopup';
import ImagesetPanorama from './ImagesetPanorama';

export default {
  name: 'ImagesetGroup',
  components: {
    Status,
    ImageItem,
    ImagesetPopup,
    ImagesetPanorama,
    ReportInspector
  },
  mixins: [mobileScreenControl],
  props: {
    store: {
      type: Object,
      default: () => ({})
    },
    imageset: {
      type: Object,
      default: () => ({})
    },
    imageBoxWidth: {
      type: Number,
      default: 250
    }
  },
  data() {
    return {
      reportInspector: {
        dialog: false,
        loading: false,
        mounted: false,
        skusData: [],
        data: {}
      },
      activeImage: {},
      popupStatus: false,
      panoramaPopupStatus: false,
      isReportInspectorRequested: false
    };
  },
  computed: {
    ...mapState(['Project', 'Account']),
    isReportInspectorActive() {
      return this.imageset.report_status === 'reported' && (this.isDeveloper || this.Project.active.data.settings.report_inspector);
    },
    isDeveloper() {
      return this.Account.active.data.developer;
    },
    projectId() {
      return this.$route.params.projectId;
    },
    visitId() {
      return this.store.id;
    },
    reportSkusIDList() {
      return this.imageset.sku_ids;
    },
    imagesetLink() {
      return {
        name: breadcrumbs.imagesetDetail,
        params: {
          visitId: this.store.id,
          imagesetId: this.imageset.id
        }
      };
    }
  },
  created() {
    this.reportInspector.data = this.imageset;
  },
  methods: {
    openPopup(image) {
      this.activeImage = image;
      this.popupStatus = true;
    },
    async openInspector(openDialogFunc) {
      this.reportInspector.loading = true;

      if (!this.reportInspector.mounted) {
        await this.fetchSkusData();
        await this.fetchData();
      }

      openDialogFunc();

      this.reportInspector.mounted = true;
      this.reportInspector.loading = false;
    },
    async fetchSkusData() {
      try {
        const { data: { data } } = await fetchSkus(this.projectId, { search: { ids: this.reportSkusIDList.toString() }, per: 5000 });

        this.reportInspector.skusData = data.map(sku => ({
          ...sku,
          images: sku.images ? sku.images.split('?')[0] : ''
        }));
      } catch (error) {
        console.warn(error);
      }
    },
    async fetchData() {
      const { data } = await fetchImageset(this.projectId, this.imageset.id);

      this.reportInspector.data = data.data;
    },
    reqReportInspector() {
      this.isReportInspectorRequested = true;

      setTimeout(() => {
        this.$refs.reportInspectorButton.click();
      }, 0);
    }
  }
};
</script>

<style lang="scss">
.imageset-group {
  padding: 10px;
  background: #fff;
  border-bottom: 2px solid #f1f1f1;
  border-left: 2px solid #f1f1f1;

  &__header {
    display: flex;
    gap: 5px;
    align-items: flex-start;
    justify-content: space-between;
    height: 50px;
  }

  &__info {
    flex-grow: 1;
  }

  &__title {
    display: inline-block;
    padding: 5px 7px;
    font-size: 11px;
    font-weight: 600;
    color: #fff;
    background: $greyShingle;
    border-radius: 50px;
  }

  &__subtitle {
    padding: 5px 7px;
    font-size: 11px;
    font-weight: 600;
    color: $greyShingle;
  }

  &__buttons {
    display: flex;
  }

  &__button {
    padding: 5px;
    background: none !important;
    border: none;
    border-radius: 0;
    outline: 0;

    &:nth-child(2) {
      padding-left: 10px;
      border-left: 1px solid #ddd;
    }

    img {
      width: 27px !important;
      height: auto;
    }

    svg {
      width: 22px !important;
      height: 25px !important;
      color: #aaa !important;
    }
  }

  &__list {
    display: flex;
    gap: 10px;
  }

  .panorama-btn {
    padding: 0 !important;
    color: $greyShingle !important;
  }
}

.imageset-dialog {
  .el-dialog {
    border-radius: 4px;

    &__header {
      display: none;
    }

    &__body {
      padding: 30px 0;
    }
  }
}
</style>
